/**
 * Timeline Filters Component
 * PROJ-024 - Fase 1: Quick Wins
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Filter, X } from 'lucide-react';
import type { TimelineEventKind } from '@/types/timeline';

const FILTER_OPTIONS: Array<{ value: TimelineEventKind; label: string; icon: string; category: string }> = [
  // Lifecycle
  { value: 'created', label: 'Creado', icon: '🆕', category: 'Ciclo de Vida' },
  { value: 'updated', label: 'Actualizado', icon: '📝', category: 'Ciclo de Vida' },
  { value: 'state_changed', label: 'Cambio de Estado', icon: '➡️', category: 'Ciclo de Vida' },
  { value: 'severity_changed', label: 'Cambio de Severidad', icon: '⚠️', category: 'Ciclo de Vida' },

  // Assignments
  { value: 'assigned', label: 'Asignado', icon: '👤', category: 'Asignaciones' },
  { value: 'reassigned', label: 'Reasignado', icon: '🔄', category: 'Asignaciones' },

  // Resolution
  { value: 'resolved', label: 'Resuelto', icon: '✅', category: 'Resolución' },
  { value: 'closed', label: 'Cerrado', icon: '🔒', category: 'Resolución' },
  { value: 'reopened', label: 'Reabierto', icon: '🔓', category: 'Resolución' },
  { value: 'cancelled', label: 'Cancelado', icon: '❌', category: 'Resolución' },

  // Parties
  { value: 'party_added', label: 'Parte Agregada', icon: '👥', category: 'Partes' },
  { value: 'party_removed', label: 'Parte Removida', icon: '👤❌', category: 'Partes' },

  // Evidence
  { value: 'evidence_added', label: 'Evidencia Agregada', icon: '📎', category: 'Evidencias' },
  { value: 'evidence_removed', label: 'Evidencia Removida', icon: '📎❌', category: 'Evidencias' },

  // Notes
  { value: 'note_added', label: 'Nota Agregada', icon: '📝', category: 'Notas' },
  { value: 'note_deleted', label: 'Nota Eliminada', icon: '🗑️', category: 'Notas' },

  // Tasks
  { value: 'task_created', label: 'Tarea Creada', icon: '☑️', category: 'Tareas' },
  { value: 'task_completed', label: 'Tarea Completada', icon: '✔️', category: 'Tareas' },

  // Comments & System
  { value: 'comment', label: 'Comentario', icon: '💬', category: 'Comentarios' },
  { value: 'sla_warning', label: 'Alerta SLA', icon: '⚠️', category: 'Sistema' },
  { value: 'sla_breach', label: 'Incumplimiento SLA', icon: '🔴', category: 'Sistema' },
  { value: 'auto_escalation', label: 'Escalación Automática', icon: '⬆️', category: 'Sistema' }
];

interface TimelineFiltersProps {
  onFilterChange: (selectedKinds: Set<TimelineEventKind>) => void;
}

export function TimelineFilters({ onFilterChange }: TimelineFiltersProps) {
  const [selectedFilters, setSelectedFilters] = useState<Set<TimelineEventKind>>(new Set());

  const handleToggle = (kind: TimelineEventKind) => {
    const newFilters = new Set(selectedFilters);

    if (newFilters.has(kind)) {
      newFilters.delete(kind);
    } else {
      newFilters.add(kind);
    }

    setSelectedFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleClearAll = () => {
    setSelectedFilters(new Set());
    onFilterChange(new Set());
  };

  // Agrupar opciones por categoría
  const categories = Array.from(new Set(FILTER_OPTIONS.map(o => o.category)));

  return (
    <div className="flex items-center gap-4 flex-wrap">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Filter className="mr-2 h-4 w-4" />
            Filtros
            {selectedFilters.size > 0 && (
              <Badge variant="secondary" className="ml-2">
                {selectedFilters.size}
              </Badge>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-72" align="start">
          {categories.map((category, idx) => {
            const categoryOptions = FILTER_OPTIONS.filter(o => o.category === category);
            return (
              <div key={category}>
                {idx > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="text-xs text-muted-foreground">
                  {category}
                </DropdownMenuLabel>
                {categoryOptions.map(option => (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={selectedFilters.has(option.value)}
                    onCheckedChange={() => handleToggle(option.value)}
                  >
                    <span className="mr-2">{option.icon}</span>
                    {option.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </div>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Badges de filtros activos */}
      {selectedFilters.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {Array.from(selectedFilters).map(kind => {
            const option = FILTER_OPTIONS.find(o => o.value === kind);
            if (!option) return null;

            return (
              <Badge
                key={kind}
                variant="secondary"
                className="cursor-pointer hover:bg-secondary/80"
                onClick={() => handleToggle(kind)}
              >
                <span className="mr-1">{option.icon}</span>
                {option.label}
                <X className="ml-1 h-3 w-3" />
              </Badge>
            );
          })}

          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            className="h-6 text-xs"
          >
            Limpiar filtros
          </Button>
        </div>
      )}
    </div>
  );
}
